import { useEffect, useMemo, useState } from 'react'
import type { BookingStaffHour, BookingStaffSpecialHour, BookingStaffTimeOff } from '../../types/bookings'

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

type DayRange = {
  opens_at: string
  closes_at: string
}

type Props = {
  title: string
  staffLabel: string
  staffId: number | null
  loadHours: (staffId: number) => Promise<BookingStaffHour[]>
  saveHours: (staffId: number, rows: Array<{ weekday: number; opens_at: string; closes_at: string }>) => Promise<BookingStaffHour[]>
  loadSpecialHours: (staffId: number) => Promise<BookingStaffSpecialHour[]>
  saveSpecialHour: (staffId: number, payload: { date: string; is_closed: boolean; opens_at?: string | null; closes_at?: string | null; note?: string }) => Promise<BookingStaffSpecialHour>
  deleteSpecialHour: (staffId: number, specialId: number) => Promise<void>
  loadTimeOff: (staffId: number) => Promise<BookingStaffTimeOff[]>
  createTimeOff: (staffId: number, payload: { starts_at: string; ends_at: string; note?: string }) => Promise<BookingStaffTimeOff>
  deleteTimeOff: (staffId: number, timeoffId: number) => Promise<void>
  onToast: (kind: 'success' | 'error', label: string) => void
}

export default function StaffScheduleEditor({
  title,
  staffLabel,
  staffId,
  loadHours,
  saveHours,
  loadSpecialHours,
  saveSpecialHour,
  deleteSpecialHour,
  loadTimeOff,
  createTimeOff,
  deleteTimeOff,
  onToast,
}: Props) {
  const [activeTab, setActiveTab] = useState<'weekly' | 'special' | 'timeoff'>('weekly')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [hoursByDay, setHoursByDay] = useState<Record<number, DayRange[]>>(() => ({ 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] }))
  const [savingHours, setSavingHours] = useState(false)

  const [specialHours, setSpecialHours] = useState<BookingStaffSpecialHour[]>([])
  const [specialDate, setSpecialDate] = useState('')
  const [specialClosed, setSpecialClosed] = useState(true)
  const [specialOpen, setSpecialOpen] = useState('09:00')
  const [specialClose, setSpecialClose] = useState('17:00')
  const [specialNote, setSpecialNote] = useState('')
  const [savingSpecial, setSavingSpecial] = useState(false)
  const [deletingSpecialId, setDeletingSpecialId] = useState<number | null>(null)

  const [timeOffRows, setTimeOffRows] = useState<BookingStaffTimeOff[]>([])
  const [timeoffStart, setTimeoffStart] = useState('')
  const [timeoffEnd, setTimeoffEnd] = useState('')
  const [timeoffNote, setTimeoffNote] = useState('')
  const [savingTimeoff, setSavingTimeoff] = useState(false)
  const [deletingTimeoffId, setDeletingTimeoffId] = useState<number | null>(null)

  useEffect(() => {
    if (!staffId) return
    let live = true
    const run = async () => {
      setLoading(true)
      setError(null)
      try {
        const [hours, specials, timeoff] = await Promise.all([
          loadHours(staffId),
          loadSpecialHours(staffId),
          loadTimeOff(staffId),
        ])
        if (!live) return
        const grouped: Record<number, DayRange[]> = { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] }
        for (const row of hours) {
          grouped[row.weekday] = [...(grouped[row.weekday] || []), { opens_at: row.opens_at.slice(0, 5), closes_at: row.closes_at.slice(0, 5) }]
        }
        setHoursByDay(grouped)
        setSpecialHours(specials)
        setTimeOffRows(timeoff)
      } catch (e: any) {
        if (live) setError(e?.response?.data?.detail || e?.message || 'Failed to load staff schedule.')
      } finally {
        if (live) setLoading(false)
      }
    }
    void run()
    return () => {
      live = false
    }
  }, [staffId, loadHours, loadSpecialHours, loadTimeOff])

  const totalWeeklyRanges = useMemo(
    () => Object.values(hoursByDay).reduce((sum, list) => sum + list.length, 0),
    [hoursByDay],
  )

  if (!staffId) {
    return <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-white/70">Select a staff member to edit schedule.</div>
  }

  const onAddRange = (weekday: number) => {
    setHoursByDay((prev) => ({ ...prev, [weekday]: [...(prev[weekday] || []), { opens_at: '09:00', closes_at: '17:00' }] }))
  }

  const onRemoveRange = (weekday: number, idx: number) => {
    setHoursByDay((prev) => ({ ...prev, [weekday]: (prev[weekday] || []).filter((_, i) => i !== idx) }))
  }

  const onChangeRange = (weekday: number, idx: number, field: keyof DayRange, value: string) => {
    setHoursByDay((prev) => ({
      ...prev,
      [weekday]: (prev[weekday] || []).map((row, i) => (i === idx ? { ...row, [field]: value } : row)),
    }))
  }

  const onSaveWeekly = async () => {
    if (!staffId || savingHours) return
    const rows: Array<{ weekday: number; opens_at: string; closes_at: string }> = []
    for (let day = 0; day < 7; day += 1) {
      for (const row of hoursByDay[day] || []) {
        if (!row.opens_at || !row.closes_at || row.closes_at <= row.opens_at) {
          onToast('error', 'Fix weekly hour ranges before saving.')
          return
        }
        rows.push({ weekday: day, opens_at: row.opens_at, closes_at: row.closes_at })
      }
    }
    setSavingHours(true)
    try {
      const saved = await saveHours(staffId, rows)
      const grouped: Record<number, DayRange[]> = { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] }
      for (const row of saved) {
        grouped[row.weekday] = [...(grouped[row.weekday] || []), { opens_at: row.opens_at.slice(0, 5), closes_at: row.closes_at.slice(0, 5) }]
      }
      setHoursByDay(grouped)
      onToast('success', 'Staff weekly hours saved')
    } catch (e: any) {
      onToast('error', e?.response?.data?.detail || e?.message || 'Failed to save weekly hours.')
    } finally {
      setSavingHours(false)
    }
  }

  const onSaveSpecial = async () => {
    if (!staffId || !specialDate || savingSpecial) return
    if (!specialClosed && (!specialOpen || !specialClose || specialClose <= specialOpen)) {
      onToast('error', 'Special hours require valid open and close times.')
      return
    }
    setSavingSpecial(true)
    try {
      const row = await saveSpecialHour(staffId, {
        date: specialDate,
        is_closed: specialClosed,
        opens_at: specialClosed ? null : specialOpen,
        closes_at: specialClosed ? null : specialClose,
        note: specialNote.trim(),
      })
      setSpecialHours((prev) => {
        const next = [...prev.filter((item) => item.id !== row.id && item.date !== row.date), row]
        return next.sort((a, b) => String(a.date).localeCompare(String(b.date)))
      })
      onToast('success', 'Special hour saved')
    } catch (e: any) {
      onToast('error', e?.response?.data?.detail || e?.message || 'Failed to save special hour.')
    } finally {
      setSavingSpecial(false)
    }
  }

  const onDeleteSpecial = async (row: BookingStaffSpecialHour) => {
    if (!staffId || deletingSpecialId != null) return
    setDeletingSpecialId(row.id)
    try {
      await deleteSpecialHour(staffId, row.id)
      setSpecialHours((prev) => prev.filter((item) => item.id !== row.id))
      onToast('success', 'Special hour deleted')
    } catch (e: any) {
      onToast('error', e?.response?.data?.detail || e?.message || 'Failed to delete special hour.')
    } finally {
      setDeletingSpecialId(null)
    }
  }

  const onAddTimeoff = async () => {
    if (!staffId || !timeoffStart || !timeoffEnd || savingTimeoff) return
    if (timeoffEnd <= timeoffStart) {
      onToast('error', 'Time off end must be after start.')
      return
    }
    setSavingTimeoff(true)
    try {
      const row = await createTimeOff(staffId, {
        starts_at: new Date(timeoffStart).toISOString(),
        ends_at: new Date(timeoffEnd).toISOString(),
        note: timeoffNote.trim(),
      })
      setTimeOffRows((prev) => [...prev, row].sort((a, b) => String(a.starts_at).localeCompare(String(b.starts_at))))
      setTimeoffStart('')
      setTimeoffEnd('')
      setTimeoffNote('')
      onToast('success', 'Time off added')
    } catch (e: any) {
      onToast('error', e?.response?.data?.detail || e?.message || 'Failed to add time off.')
    } finally {
      setSavingTimeoff(false)
    }
  }

  const onDeleteTimeoff = async (row: BookingStaffTimeOff) => {
    if (!staffId || deletingTimeoffId != null) return
    setDeletingTimeoffId(row.id)
    try {
      await deleteTimeOff(staffId, row.id)
      setTimeOffRows((prev) => prev.filter((item) => item.id !== row.id))
      onToast('success', 'Time off removed')
    } catch (e: any) {
      onToast('error', e?.response?.data?.detail || e?.message || 'Failed to remove time off.')
    } finally {
      setDeletingTimeoffId(null)
    }
  }

  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-sm font-semibold">{title}</div>
          <div className="text-xs text-white/65">{staffLabel}</div>
        </div>
        <div className="inline-flex rounded-lg border border-white/10 bg-black/30 p-1 text-xs">
          {(['weekly', 'special', 'timeoff'] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`rounded-md px-2.5 py-1 font-semibold ${activeTab === tab ? 'bg-brand text-white' : 'text-white/75 hover:bg-white/10'}`}
            >
              {tab === 'weekly' ? 'Weekly Hours' : tab === 'special' ? 'Special Hours' : 'Time Off'}
            </button>
          ))}
        </div>
      </div>

      {error ? <div className="mt-2 text-xs text-red-300">{error}</div> : null}
      {loading ? <div className="mt-3 text-sm text-white/70">Loading schedule...</div> : null}

      {!loading && activeTab === 'weekly' ? (
        <div className="mt-3 space-y-3">
          <div className="text-xs text-white/60">{totalWeeklyRanges} weekly range(s) configured.</div>
          {WEEKDAY_LABELS.map((label, weekday) => (
            <div key={label} className="rounded-lg border border-white/10 bg-black/30 p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-semibold">{label}</div>
                <button type="button" onClick={() => onAddRange(weekday)} className="rounded-lg border border-white/15 bg-white/5 px-2 py-1 text-xs font-semibold hover:bg-white/10">
                  Add range
                </button>
              </div>
              <div className="mt-2 space-y-2">
                {(hoursByDay[weekday] || []).length === 0 ? <div className="text-xs text-white/60">Closed</div> : null}
                {(hoursByDay[weekday] || []).map((row, idx) => (
                  <div key={`${label}-${idx}`} className="grid grid-cols-[1fr_1fr_auto] gap-2">
                    <input type="time" value={row.opens_at} onChange={(e) => onChangeRange(weekday, idx, 'opens_at', e.target.value)} className="rounded-lg border border-white/10 bg-black/20 px-2 py-1.5 text-xs" />
                    <input type="time" value={row.closes_at} onChange={(e) => onChangeRange(weekday, idx, 'closes_at', e.target.value)} className="rounded-lg border border-white/10 bg-black/20 px-2 py-1.5 text-xs" />
                    <button type="button" onClick={() => onRemoveRange(weekday, idx)} className="rounded-lg border border-red-400/30 bg-red-500/10 px-2 py-1.5 text-xs font-semibold text-red-200 hover:bg-red-500/20">
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={() => void onSaveWeekly()}
            disabled={savingHours}
            className={`rounded-lg px-3 py-2 text-sm font-semibold ${savingHours ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'bg-brand text-white hover:bg-brand-600'}`}
          >
            {savingHours ? 'Saving...' : 'Save Weekly Hours'}
          </button>
        </div>
      ) : null}

      {!loading && activeTab === 'special' ? (
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            <label className="text-xs text-white/70">
              Date
              <input type="date" value={specialDate} onChange={(e) => setSpecialDate(e.target.value)} className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-2 py-1.5 text-xs" />
            </label>
            <label className="text-xs text-white/70">
              Note
              <input value={specialNote} onChange={(e) => setSpecialNote(e.target.value)} placeholder="Holiday, event, etc." className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-2 py-1.5 text-xs" />
            </label>
          </div>
          <label className="inline-flex items-center gap-2 text-xs text-white/80">
            <input type="checkbox" checked={specialClosed} onChange={(e) => setSpecialClosed(e.target.checked)} />
            Closed for this date
          </label>
          {!specialClosed ? (
            <div className="grid grid-cols-2 gap-2">
              <input type="time" value={specialOpen} onChange={(e) => setSpecialOpen(e.target.value)} className="rounded-lg border border-white/10 bg-black/20 px-2 py-1.5 text-xs" />
              <input type="time" value={specialClose} onChange={(e) => setSpecialClose(e.target.value)} className="rounded-lg border border-white/10 bg-black/20 px-2 py-1.5 text-xs" />
            </div>
          ) : null}
          <button
            type="button"
            onClick={() => void onSaveSpecial()}
            disabled={!specialDate || savingSpecial}
            className={`rounded-lg px-3 py-2 text-sm font-semibold ${!specialDate || savingSpecial ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'bg-brand text-white hover:bg-brand-600'}`}
          >
            {savingSpecial ? 'Saving...' : 'Save Special Day'}
          </button>
          <div className="space-y-2">
            {specialHours.length === 0 ? <div className="text-xs text-white/60">No special-hour overrides yet.</div> : null}
            {specialHours.map((row) => (
              <div key={row.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-xs">
                <div>
                  <div className="font-semibold text-white">{new Date(`${row.date}T00:00:00`).toLocaleDateString()}</div>
                  <div className="text-white/70">
                    {row.is_closed ? 'Closed' : `${String(row.opens_at || '').slice(0, 5)} - ${String(row.closes_at || '').slice(0, 5)}`}
                    {row.note ? ` • ${row.note}` : ''}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => void onDeleteSpecial(row)}
                  disabled={deletingSpecialId === row.id}
                  className={`rounded-lg px-2.5 py-1.5 font-semibold ${deletingSpecialId === row.id ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'border border-red-400/30 bg-red-500/10 text-red-200 hover:bg-red-500/20'}`}
                >
                  {deletingSpecialId === row.id ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {!loading && activeTab === 'timeoff' ? (
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            <label className="text-xs text-white/70">
              Starts at
              <input type="datetime-local" value={timeoffStart} onChange={(e) => setTimeoffStart(e.target.value)} className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-2 py-1.5 text-xs" />
            </label>
            <label className="text-xs text-white/70">
              Ends at
              <input type="datetime-local" value={timeoffEnd} onChange={(e) => setTimeoffEnd(e.target.value)} className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-2 py-1.5 text-xs" />
            </label>
          </div>
          <label className="text-xs text-white/70">
            Note
            <input value={timeoffNote} onChange={(e) => setTimeoffNote(e.target.value)} placeholder="Vacation, break, etc." className="mt-1 w-full rounded-lg border border-white/10 bg-black/20 px-2 py-1.5 text-xs" />
          </label>
          <button
            type="button"
            onClick={() => void onAddTimeoff()}
            disabled={!timeoffStart || !timeoffEnd || savingTimeoff}
            className={`rounded-lg px-3 py-2 text-sm font-semibold ${!timeoffStart || !timeoffEnd || savingTimeoff ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'bg-brand text-white hover:bg-brand-600'}`}
          >
            {savingTimeoff ? 'Saving...' : 'Add Time Off'}
          </button>
          <div className="space-y-2">
            {timeOffRows.length === 0 ? <div className="text-xs text-white/60">No time off blocks yet.</div> : null}
            {timeOffRows.map((row) => (
              <div key={row.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-xs">
                <div className="text-white/75">
                  {new Date(row.starts_at).toLocaleString()} - {new Date(row.ends_at).toLocaleString()}
                  {row.note ? ` • ${row.note}` : ''}
                </div>
                <button
                  type="button"
                  onClick={() => void onDeleteTimeoff(row)}
                  disabled={deletingTimeoffId === row.id}
                  className={`rounded-lg px-2.5 py-1.5 font-semibold ${deletingTimeoffId === row.id ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'border border-red-400/30 bg-red-500/10 text-red-200 hover:bg-red-500/20'}`}
                >
                  {deletingTimeoffId === row.id ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  )
}

